'use client';

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslations } from 'next-intl';
import { api, AuditorListItem, AuditorDetails } from '@/lib/api';

interface AuditorSelectorProps {
    onSelect: (auditorId: string, auditorName: string) => void;
    selectedAuditorId?: string | null;
}

interface ExpandedAuditor extends AuditorListItem {
    details?: AuditorDetails;
    loading?: boolean;
}

export function AuditorSelector({ onSelect, selectedAuditorId }: AuditorSelectorProps) {
    const t = useTranslations('auditor');
    const [auditors, setAuditors] = useState<ExpandedAuditor[]>([]);
    const [loading, setLoading] = useState(true);
    const [expandedId, setExpandedId] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchAuditors = async () => {
            try {
                const data = await api.getAuditors();
                setAuditors(data.map(a => ({ ...a })));
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Failed to load auditors');
            } finally {
                setLoading(false);
            }
        };
        fetchAuditors();
    }, []);

    const handleToggleExpand = async (auditorId: string) => {
        if (expandedId === auditorId) {
            // Collapse
            setExpandedId(null);
            return;
        }

        // Expand and fetch details if not already loaded
        setExpandedId(auditorId);

        const auditor = auditors.find(a => a.id === auditorId);
        if (auditor && !auditor.details) {
            setAuditors(prev => prev.map(a =>
                a.id === auditorId ? { ...a, loading: true } : a
            ));

            try {
                const details = await api.getAuditorDetails(auditorId);
                setAuditors(prev => prev.map(a =>
                    a.id === auditorId ? { ...a, details, loading: false } : a
                ));
            } catch {
                setAuditors(prev => prev.map(a =>
                    a.id === auditorId ? { ...a, loading: false } : a
                ));
            }
        }
    };

    const handleSelect = (auditor: ExpandedAuditor) => {
        onSelect(auditor.id, `${auditor.first_name} ${auditor.last_name}`);
    };

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center py-12">
                <div className="animate-spin rounded-full h-10 w-10 border-2 border-slate-200 border-t-black mb-4" />
                <p className="text-slate-500 text-sm">{t('loading')}</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-rose-50 border border-rose-100 rounded-xl p-4 text-rose-600 text-sm">
                {error}
            </div>
        );
    }

    if (auditors.length === 0) {
        return (
            <div className="bg-slate-50 border border-slate-100 rounded-xl p-6 text-center">
                <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center text-slate-400 mx-auto mb-3">
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                    </svg>
                </div>
                <p className="text-slate-600 font-medium">{t('noAuditors')}</p>
                <p className="text-slate-400 text-sm mt-1">{t('noAuditorsDescription')}</p>
            </div>
        );
    }

    return (
        <div className="space-y-2">
            {auditors.map((auditor) => {
                const isExpanded = expandedId === auditor.id;
                const isSelected = selectedAuditorId === auditor.id;

                return (
                    <motion.div
                        key={auditor.id}
                        layout
                        className={`
                            border rounded-xl overflow-hidden transition-all
                            ${isSelected
                                ? 'border-violet-300 bg-violet-50/50 ring-2 ring-violet-200'
                                : 'border-slate-200 bg-white hover:border-slate-300'
                            }
                        `}
                    >
                        {/* Header - Always visible */}
                        <button
                            onClick={() => handleToggleExpand(auditor.id)}
                            className="w-full p-4 flex items-center justify-between text-left"
                            aria-expanded={isExpanded}
                        >
                            <div className="flex items-center gap-3">
                                {/* Avatar */}
                                <div className={`
                                    w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm
                                    ${isSelected
                                        ? 'bg-violet-100 text-violet-600'
                                        : 'bg-slate-100 text-slate-600'
                                    }
                                `}>
                                    {auditor.first_name.charAt(0)}{auditor.last_name.charAt(0)}
                                </div>

                                {/* Name and location */}
                                <div>
                                    <p className="font-semibold text-slate-800">
                                        {auditor.first_name} {auditor.last_name}
                                    </p>
                                    <p className="text-sm text-slate-500">
                                        {auditor.city}, {auditor.country}
                                    </p>
                                </div>
                            </div>

                            {/* Chevron */}
                            <motion.div
                                animate={{ rotate: isExpanded ? 180 : 0 }}
                                transition={{ duration: 0.2 }}
                                className="text-slate-400"
                            >
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </motion.div>
                        </button>

                        {/* Expanded details */}
                        <AnimatePresence>
                            {isExpanded && (
                                <motion.div
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: 'auto', opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    transition={{ duration: 0.2 }}
                                    className="overflow-hidden"
                                >
                                    <div className="px-4 pb-4 pt-2 border-t border-slate-100">
                                        {auditor.loading ? (
                                            <div className="flex items-center justify-center py-4">
                                                <div className="animate-spin rounded-full h-6 w-6 border-2 border-slate-200 border-t-black" />
                                            </div>
                                        ) : auditor.details ? (
                                            <div className="space-y-3">
                                                {/* Profile details grid */}
                                                <div className="grid grid-cols-2 gap-3 text-sm">
                                                    <div>
                                                        <p className="text-slate-400 text-xs uppercase tracking-wider mb-1">{t('email')}</p>
                                                        <p className="text-slate-700">{auditor.details.email}</p>
                                                    </div>
                                                    <div>
                                                        <p className="text-slate-400 text-xs uppercase tracking-wider mb-1">{t('professionalArea')}</p>
                                                        <p className="text-slate-700">{auditor.details.professional_area}</p>
                                                    </div>
                                                    <div>
                                                        {auditor.details.affiliation ? (
                                                            <>
                                                                <p className="text-slate-400 text-xs uppercase tracking-wider mb-1">{t('affiliation')}</p>
                                                                <p className="text-slate-700">{auditor.details.affiliation}</p>
                                                            </>
                                                        ) : (
                                                            <div /> /* Empty cell placeholder */
                                                        )}
                                                    </div>
                                                    <div>
                                                        {auditor.details.phone_number && (
                                                            <>
                                                                <p className="text-slate-400 text-xs uppercase tracking-wider mb-1">{t('phone')}</p>
                                                                <p className="text-slate-700">
                                                                    {auditor.details.phone_country_code} {auditor.details.phone_number}
                                                                </p>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Select button */}
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleSelect(auditor);
                                                    }}
                                                    className={`
                                                        w-full py-2.5 px-4 rounded-lg font-medium text-sm transition-all
                                                        ${isSelected
                                                            ? 'bg-violet-600 text-white shadow-lg shadow-violet-200'
                                                            : 'bg-slate-100 text-slate-700 hover:bg-violet-100 hover:text-violet-700'
                                                        }
                                                    `}
                                                >
                                                    {isSelected ? t('selected') : t('selectAuditor')}
                                                </button>
                                            </div>
                                        ) : (
                                            <p className="text-slate-400 text-sm text-center py-4">
                                                {t('failedToLoadDetails')}
                                            </p>
                                        )}
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </motion.div>
                );
            })}
        </div>
    );
}
